/**
 * Sprint 0147 — Weighbridge Corrections workspace.
 *
 * Primary audience: ACCOUNTANT (also visible to ACCOUNTS_MANAGER, CFO).
 * Shows PENDING corrections raised by Gate operators, with side-by-side
 * original vs corrected and one-click Approve/Reject.
 *
 * Approve flow:
 *   click Approve → optional notes → POST .../approve
 *   → WeighbridgeLog + WeightReconciliation updated in-place
 *   → CFO + MD audit notification fired
 *
 * Reject flow:
 *   click Reject → mandatory reason → POST .../reject
 *   → original raiser notified to fix + re-raise
 */
import { useState } from 'react'
import {
  Alert, Badge, Button, Card, Descriptions, Drawer, Input, Modal, Row, Col,
  Space, Table, Tabs, Tag, Typography, message,
} from 'antd'
import { CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import {
  STATUS_COLOR,
  type CorrectionStatus,
  type WeighbridgeCorrection,
  weighbridgeCorrectionsApi,
} from '@/features/gate/weighbridgeCorrectionsApi'

const { Text } = Typography

function fmtKg(v: number | null) {
  if (v == null) return '—'
  return `${Number(v).toFixed(3)} kg`
}
function fmtPct(v: number | null) {
  if (v == null) return '—'
  return `${Number(v).toFixed(2)}%`
}
function fmtRs(v: number | null) {
  if (v == null) return '—'
  return `₹${Number(v).toLocaleString('en-IN')}`
}

function Diff({ before, after, fmt }: {
  before: number | null
  after: number | null
  fmt: (v: number | null) => string
}) {
  if (after == null) return <Text type="secondary">—</Text>
  const changed = before == null || Number(before) !== Number(after)
  return (
    <Space size={6}>
      <Text delete={changed} type={changed ? 'secondary' : undefined}>
        {fmt(before)}
      </Text>
      {changed && (
        <>
          <Text type="secondary">→</Text>
          <Text strong style={{ color: '#1677ff' }}>{fmt(after)}</Text>
        </>
      )}
    </Space>
  )
}

export default function WeighbridgeCorrectionsPage() {
  const canApprove = usePermission('weighbridge.correction.approve')
  const qc = useQueryClient()
  const [statusTab, setStatusTab] = useState<CorrectionStatus | 'ALL'>('PENDING')
  const [viewing, setViewing] = useState<WeighbridgeCorrection | null>(null)
  const [rejecting, setRejecting] = useState<WeighbridgeCorrection | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const listQ = useQuery({
    queryKey: ['wb-corrections', statusTab],
    queryFn: () => weighbridgeCorrectionsApi.list({
      status: statusTab, limit: 200,
    }),
    refetchInterval: statusTab === 'PENDING' ? 15000 : false,
  })

  const approveMut = useMutation({
    mutationFn: (row: WeighbridgeCorrection) =>
      weighbridgeCorrectionsApi.approve(row.id),
    onSuccess: (data) => {
      message.success(`${data.correction_no} approved. CFO + MD notified.`)
      qc.invalidateQueries({ queryKey: ['wb-corrections'] })
      setViewing(null)
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } }
      message.error(e?.response?.data?.detail ?? 'Approve failed')
    },
  })

  const rejectMut = useMutation({
    mutationFn: ({ row, reason }: { row: WeighbridgeCorrection; reason: string }) =>
      weighbridgeCorrectionsApi.reject(row.id, reason),
    onSuccess: (data) => {
      message.success(`${data.correction_no} rejected. Raiser notified.`)
      qc.invalidateQueries({ queryKey: ['wb-corrections'] })
      setRejecting(null); setRejectReason('')
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } }
      message.error(e?.response?.data?.detail ?? 'Reject failed')
    },
  })

  const columns = [
    {
      title: 'No', dataIndex: 'correction_no', key: 'no', width: 140,
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: 'Inward', dataIndex: 'inward_no', key: 'inward', width: 140,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Type', dataIndex: 'correction_type', key: 'type', width: 110,
      render: (v: string) => <Tag color="blue">{v.replace(/_/g, ' ')}</Tag>,
    },
    {
      title: 'Net change', key: 'net_change', width: 180,
      render: (_: unknown, r: WeighbridgeCorrection) => (
        <Diff before={r.original_net_kg} after={r.corrected_net_kg} fmt={fmtKg} />
      ),
    },
    {
      title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true,
    },
    {
      title: 'Raised', dataIndex: 'raised_at', key: 'raised', width: 140,
      render: (v: string) => dayjs(v).fromNow(),
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (s: CorrectionStatus) => (
        <Badge status={STATUS_COLOR[s] as never} text={s} />
      ),
    },
    {
      title: 'Actions', key: 'act', width: 220,
      render: (_: unknown, r: WeighbridgeCorrection) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />}
            onClick={() => setViewing(r)} />
          {r.status === 'PENDING' && canApprove && (
            <>
              <Button size="small" type="primary"
                icon={<CheckOutlined />}
                loading={approveMut.isPending && approveMut.variables?.id === r.id}
                onClick={() => approveMut.mutate(r)}
              >Approve</Button>
              <Button size="small" danger
                icon={<CloseOutlined />}
                onClick={() => { setRejecting(r); setRejectReason('') }}
              >Reject</Button>
            </>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Weighbridge Corrections"
        subtitle={
          canApprove
            ? "Approve or reject weighbridge mistakes raised by Gate. CFO + MD audit on every approval."
            : "Track weighbridge correction status. Only Accountant / Accounts Manager / CFO can approve."
        }
      />

      {statusTab === 'PENDING' && listQ.data && listQ.data.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${listQ.data.length} pending correction${listQ.data.length === 1 ? '' : 's'} — audit-worthy money movement.`}
          description="Same user who raised it cannot approve. Each approval notifies CFO + MD."
        />
      )}

      <Card size="small">
        <Tabs
          activeKey={statusTab}
          onChange={(k) => setStatusTab(k as CorrectionStatus | 'ALL')}
          items={[
            { key: 'PENDING', label: 'Pending' },
            { key: 'APPROVED', label: 'Approved' },
            { key: 'REJECTED', label: 'Rejected' },
            { key: 'ALL', label: 'All' },
          ]}
        />
        <Table
          rowKey="id"
          loading={listQ.isLoading}
          dataSource={listQ.data ?? []}
          columns={columns}
          size="small"
          pagination={{ pageSize: 50 }}
        />
      </Card>

      {/* View / Approve drawer */}
      <Drawer
        title={viewing ? `Correction: ${viewing.correction_no}` : ''}
        width={680}
        open={!!viewing}
        onClose={() => setViewing(null)}
        extra={viewing && viewing.status === 'PENDING' && canApprove && (
          <Space>
            <Button danger icon={<CloseOutlined />}
              onClick={() => { setRejecting(viewing); setRejectReason('') }}
            >Reject</Button>
            <Button type="primary" icon={<CheckOutlined />}
              loading={approveMut.isPending}
              onClick={() => approveMut.mutate(viewing)}
            >Approve</Button>
          </Space>
        )}
      >
        {viewing && (
          <>
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={12}>
                <Card size="small" title="Original" style={{ background: '#fafafa' }}>
                  <Descriptions size="small" column={1} colon>
                    <Descriptions.Item label="Gross">{fmtKg(viewing.original_gross_kg)}</Descriptions.Item>
                    <Descriptions.Item label="Tare">{fmtKg(viewing.original_tare_kg)}</Descriptions.Item>
                    <Descriptions.Item label="Net">{fmtKg(viewing.original_net_kg)}</Descriptions.Item>
                    <Descriptions.Item label="Moisture">{fmtPct(viewing.original_moisture_pct)}</Descriptions.Item>
                    <Descriptions.Item label="Rate ₹/kg">{viewing.original_rate_per_kg ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="Freight">{fmtRs(viewing.original_freight_charge)}</Descriptions.Item>
                    <Descriptions.Item label="Other">{fmtRs(viewing.original_other_charges)}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Corrected" style={{ background: '#e6f4ff' }}>
                  <Descriptions size="small" column={1} colon>
                    <Descriptions.Item label="Gross">{fmtKg(viewing.corrected_gross_kg)}</Descriptions.Item>
                    <Descriptions.Item label="Tare">{fmtKg(viewing.corrected_tare_kg)}</Descriptions.Item>
                    <Descriptions.Item label="Net">{fmtKg(viewing.corrected_net_kg)}</Descriptions.Item>
                    <Descriptions.Item label="Moisture">{fmtPct(viewing.corrected_moisture_pct)}</Descriptions.Item>
                    <Descriptions.Item label="Rate ₹/kg">{viewing.corrected_rate_per_kg ?? '—'}</Descriptions.Item>
                    <Descriptions.Item label="Freight">{fmtRs(viewing.corrected_freight_charge)}</Descriptions.Item>
                    <Descriptions.Item label="Other">{fmtRs(viewing.corrected_other_charges)}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            </Row>
            <Descriptions size="small" column={2} colon bordered>
              <Descriptions.Item label="Inward No">{viewing.inward_no ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Type"><Tag color="blue">{viewing.correction_type}</Tag></Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge status={STATUS_COLOR[viewing.status] as never} text={viewing.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Raised">{dayjs(viewing.raised_at).format('DD-MMM HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Reason" span={2}>{viewing.reason}</Descriptions.Item>
              {viewing.approved_at && (
                <Descriptions.Item label={viewing.status === 'REJECTED' ? 'Rejected' : 'Approved'}>
                  {dayjs(viewing.approved_at).format('DD-MMM HH:mm')}
                </Descriptions.Item>
              )}
              {viewing.approver_notes && (
                <Descriptions.Item label="Notes" span={2}>{viewing.approver_notes}</Descriptions.Item>
              )}
              {viewing.reject_reason && (
                <Descriptions.Item label="Reject reason" span={2}>{viewing.reject_reason}</Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}
      </Drawer>

      {/* Reject reason modal */}
      <Modal
        title={rejecting ? `Reject ${rejecting.correction_no}?` : ''}
        open={!!rejecting}
        onCancel={() => { setRejecting(null); setRejectReason('') }}
        onOk={() => rejecting && rejectMut.mutate({ row: rejecting, reason: rejectReason })}
        okButtonProps={{
          danger: true,
          disabled: !rejectReason.trim(),
          loading: rejectMut.isPending,
        }}
        okText="Reject correction"
      >
        <Text type="secondary">
          Reason is mandatory. The original raiser ({rejecting?.correction_no})
          will be notified and can fix + re-raise.
        </Text>
        <Input.TextArea
          rows={4}
          style={{ marginTop: 12 }}
          placeholder="E.g. Corrected gross matches original — no effective change. Or: Need scale log for new value."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  )
}
